import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const INDUSTRIES = ["Tech","Healthcare","Bio","Agriculture","Education","Arts","Community","Rural","Manufacturing","Retail","Energy","Environmental","Human Services","Housing","Food","Other"];

export default function Onboarding() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    org_name: "", org_type: "llc", state: "CA", industry: "Tech",
    mission: "", years_operating: 1, team_size: 3, ein: "", website: "",
  });
  const [loading, setLoading] = useState(false);

  const change = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/api/auth/profile", form);
      toast.success("Profile saved. Let's find your grants.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  const skip = () => nav("/dashboard");

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 lg:px-8 py-14">
        <div className="overline mb-3">STEP 1 OF 1</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Tell us about your organization.</h1>
        <p className="text-neutral-600 mb-8">This context powers your grant match scores and AI-drafted documents. It stays private.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="onboarding-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Organization name</label>
              <input required value={form.org_name} onChange={(e) => change("org_name", e.target.value)}
                className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3" placeholder="Acme LLC" data-testid="onb-org" />
            </div>
            <div>
              <label className="text-sm font-medium">Org type</label>
              <Select value={form.org_type} onValueChange={(v) => change("org_type", v)}>
                <SelectTrigger className="mt-1 h-11" data-testid="onb-org-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="nonprofit">Nonprofit (501c3)</SelectItem>
                  <SelectItem value="sole_prop">Sole Prop</SelectItem>
                  <SelectItem value="corp">Corporation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">State</label>
              <Select value={form.state} onValueChange={(v) => change("state", v)}>
                <SelectTrigger className="mt-1 h-11" data-testid="onb-state"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Industry</label>
              <Select value={form.industry} onValueChange={(v) => change("industry", v)}>
                <SelectTrigger className="mt-1 h-11" data-testid="onb-industry"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {INDUSTRIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Years operating</label>
              <input type="number" min={0} value={form.years_operating} onChange={(e) => change("years_operating", +e.target.value)}
                className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3" data-testid="onb-years" />
            </div>
            <div>
              <label className="text-sm font-medium">Team size</label>
              <input type="number" min={1} value={form.team_size} onChange={(e) => change("team_size", +e.target.value)}
                className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3" data-testid="onb-team" />
            </div>
            <div>
              <label className="text-sm font-medium">EIN (optional)</label>
              <input value={form.ein} onChange={(e) => change("ein", e.target.value)}
                className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3" placeholder="XX-XXXXXXX" data-testid="onb-ein" />
            </div>
            <div>
              <label className="text-sm font-medium">Website (optional)</label>
              <input value={form.website} onChange={(e) => change("website", e.target.value)}
                className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3" placeholder="https://" data-testid="onb-website" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Mission / what you do (~2 sentences)</label>
            <textarea rows={4} value={form.mission} onChange={(e) => change("mission", e.target.value)}
              className="mt-1 w-full border border-neutral-200 rounded-lg p-3" placeholder="We help..." data-testid="onb-mission" />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button disabled={loading} className="btn-primary" data-testid="onb-submit">Save & continue</button>
            <button type="button" onClick={skip} className="btn-ghost" data-testid="onb-skip">Skip for now</button>
          </div>
        </form>
      </div>
    </div>
  );
}
